/**
 * PoolFahrzeugStatusWidget — Zeigt Verfügbarkeit des BMW iX3 (Poolfahrzeug)
 * mit Quick-Link zur Reservierungsseite.
 * Issue #1017: Dashboard-Widget: Poolfahrzeug-Status + KPIs Gesamtflotte
 */

import { useEffect, useState } from 'react';
import { Car, CalendarClock, ArrowRight, RefreshCw } from 'lucide-react';
import { useLocation } from 'wouter';
import { supabase } from '@/lib/supabase';
import {
  getPoolFahrzeugStatus,
  formatDatumKurz,
  type PoolReservierungMinimal,
  type PoolStatusInfo,
} from '@/lib/fahrzeuge-dashboard-widgets';

const KOOP_RED = '#C41E3A';

interface PoolFahrzeugData {
  id: string;
  marke: string;
  modell: string;
  kennzeichen: string | null;
}

function StatusBadge({ status }: { status: PoolStatusInfo }) {
  if (status.typ === 'frei') {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
        Frei
      </span>
    );
  }
  if (status.typ === 'belegt') {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-50 text-red-700 border border-red-200">
        Belegt bis {formatDatumKurz(status.bisWann ?? null)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-orange-50 text-orange-700 border border-orange-200">
      Reserviert ab {formatDatumKurz(status.abWann ?? null)}
    </span>
  );
}

function usePoolFahrzeugIX3() {
  const [fahrzeug, setFahrzeug] = useState<PoolFahrzeugData | null>(null);
  const [status, setStatus] = useState<PoolStatusInfo>({ typ: 'frei' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data: fz, error: fzErr } = await supabase
          .from('fahrzeuge')
          .select('id, marke, modell, kennzeichen')
          .eq('nutzungsart', 'pool')
          .eq('marke', 'BMW')
          .eq('modell', 'iX3')
          .eq('status', 'aktiv')
          .limit(1)
          .maybeSingle();

        if (fzErr || !fz || cancelled) return;
        setFahrzeug(fz as PoolFahrzeugData);

        const now = new Date();
        const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

        const { data: reservierungen } = await supabase
          .from('pool_reservierungen')
          .select('beginn, ende, zweck')
          .eq('fahrzeug_id', fz.id)
          .is('storniert_am', null)
          .not('beginn', 'is', null)
          .gte('ende', now.toISOString())
          .lte('beginn', in30Days.toISOString())
          .order('beginn')
          .limit(2);

        if (cancelled) return;

        const res = (reservierungen ?? []) as PoolReservierungMinimal[];
        const aktuelleRes = res.find(
          (r) =>
            r.beginn &&
            r.ende &&
            now >= new Date(r.beginn) &&
            now < new Date(r.ende),
        ) ?? null;
        const naechsteRes = res.find(
          (r) => r.beginn && new Date(r.beginn) > now,
        ) ?? null;

        setStatus(getPoolFahrzeugStatus(aktuelleRes, naechsteRes));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return { fahrzeug, status, loading };
}

export default function PoolFahrzeugStatusWidget() {
  const [, setLocation] = useLocation();
  const { fahrzeug, status, loading } = usePoolFahrzeugIX3();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6" data-testid="pool-status-loading">
        <RefreshCw className="w-4 h-4 animate-spin text-gray-300" />
      </div>
    );
  }

  if (!fahrzeug) {
    return (
      <div className="py-5 text-center" data-testid="pool-status-empty">
        <Car className="w-7 h-7 text-gray-200 mx-auto mb-1.5" />
        <p className="text-xs text-gray-400">BMW iX3 nicht gefunden</p>
      </div>
    );
  }

  return (
    <div data-testid="pool-fahrzeug-status-widget">
      {/* Fahrzeug-Bezeichnung */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-bold text-gray-900">
            {fahrzeug.marke} {fahrzeug.modell}
          </p>
          {fahrzeug.kennzeichen && (
            <p className="text-[10px] text-gray-400 font-mono mt-0.5">{fahrzeug.kennzeichen}</p>
          )}
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Status-Detail */}
      <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3 mb-4">
        {status.typ === 'frei' && (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
            <p className="text-xs text-gray-600">Heute frei — keine Reservierungen</p>
          </div>
        )}
        {status.typ === 'belegt' && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
              <p className="text-xs text-gray-700">
                Belegt bis <span className="font-semibold">{formatDatumKurz(status.bisWann ?? null)}</span>
              </p>
            </div>
            {status.zweck && (
              <p className="text-[10px] text-gray-400 pl-5 truncate">{status.zweck}</p>
            )}
          </div>
        )}
        {status.typ === 'reserviert' && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
              <p className="text-xs text-gray-700">
                Reserviert ab <span className="font-semibold">{formatDatumKurz(status.abWann ?? null)}</span>
              </p>
            </div>
            {status.zweck && (
              <p className="text-[10px] text-gray-400 pl-5 truncate">{status.zweck}</p>
            )}
          </div>
        )}
      </div>

      {/* Quick-Link */}
      <button
        data-testid="pool-jetzt-reservieren"
        onClick={() => setLocation('/pool-fahrzeuge')}
        disabled={status.typ === 'belegt'}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        style={{ backgroundColor: KOOP_RED }}
      >
        <Car className="w-3.5 h-3.5" />
        Jetzt reservieren
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
